import type { Breadcrumb, ErrorEvent } from '@sentry/angular';
import {
  filterSentryError,
  filterSentryBreadcrumb,
  sentryRoute,
} from './sentry-data-policy';
import { filterSentrySpan } from './sentry-trace-policy';
import {
  telemetryInternalIds,
  telemetryIdsFromUrl,
} from './telemetry-identifiers';
import { REPLAY_STATIC_CLASSES } from './replay-static-classes';

type RecordData = Record<string, unknown>;
export interface ReplayRecordingEvent {
  type: number;
  timestamp: number;
  data?: unknown;
}
export interface ReplayPolicyState {
  styleTextIds: Set<number>;
  nodes: number;
}
export const createReplayPolicyState = (): ReplayPolicyState => ({
  styleTextIds: new Set(),
  nodes: 0,
});
export const REPLAY_FINANCIAL_BLOCKS = [
  'bn-accounts-inventory',
  'bn-account-detail-dialog',
  'bn-linked-accounts-list',
  'bn-account-transactions',
  'bn-account-summary-metrics',
  'bn-spending-summary',
  'bn-credit-report-dialog',
  'bn-financial-snapshot-card',
  'bn-financial-snapshot-edit-dialog',
  'bn-financial-snapshot-monthly-summary',
  'bn-financial-snapshot-spending-mix',
  'bn-financial-snapshot-progress',
  '[class*="bn-account"]',
  '[class*="bn-credit-report"]',
  '[class*="bn-fs"]',
];

function object(value: unknown): RecordData {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as RecordData)
    : {};
}
function numeric(value: unknown): number | undefined {
  return typeof value === 'number' &&
    Number.isFinite(value) &&
    Math.abs(value) < 1e14
    ? value
    : undefined;
}
function list(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}
function mask(value: unknown): string {
  return typeof value === 'string' ? value.replace(/\S/g, '*') : '';
}
function technical(input: RecordData, names: string[]): RecordData {
  const output: RecordData = {};
  for (const key of names) {
    const value = numeric(input[key]);
    if (value !== undefined) output[key] = value;
  }
  return output;
}

/** Reparse styles with the browser's CSS grammar, then retain presentation rules only. */
function stylesheet(value: unknown): string {
  if (
    typeof value !== 'string' ||
    typeof CSSStyleSheet === 'undefined' ||
    typeof CSSStyleSheet.prototype.replaceSync !== 'function'
  )
    return '';
  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(value);
    const rules = (items: CSSRuleList): string =>
      Array.from(items)
        .map((rule) => {
          if (rule.type === 1) {
            const styleRule = rule as CSSStyleRule;
            const selector = styleRule.selectorText;
            if (/(^|[^\\])#[\w-]+/.test(selector) || /\.\\/.test(selector))
              return '';
            for (const match of selector.matchAll(
              /\.(-?[_a-zA-Z](?:[_a-zA-Z0-9-]|\\.)*)/g,
            ))
              if (!REPLAY_STATIC_CLASSES.has(match[1].replace(/\\(.)/g, '$1')))
                return '';
            for (const match of selector.matchAll(/\[([^\]]*)\]/g))
              if (
                !/^(?:_ng(?:content|host)-[\w-]+|(?:type|role|aria-expanded|aria-selected|disabled|checked)(?:[~|^$*]?=["']?(?:button|text|checkbox|radio|dialog|tab|true|false|disabled|checked)["']?)?)$/.test(
                  match[1],
                )
              )
                return '';
            const declarations: string[] = [];
            for (const property of Array.from(styleRule.style)) {
              const content = safeCssValue(
                property,
                styleRule.style.getPropertyValue(property),
              );
              if (content) declarations.push(`${property}:${content}`);
            }
            return declarations.length
              ? `${selector}{${declarations.join(';')}}`
              : '';
          }
          const group = rule as CSSGroupingRule;
          if (group.cssRules) {
            const prefix = rule.cssText
              .slice(0, rule.cssText.indexOf('{'))
              .trim();
            if (/^@(?:media|supports|layer) [a-z\d\s():.,%/-]+$/i.test(prefix))
              return `${prefix}{${rules(group.cssRules)}}`;
          }
          return ''; // No imports, font resources, content rules or arbitrary named rules.
        })
        .join('');
    return rules(sheet.cssRules);
  } catch {
    return '';
  }
}

function safeCssValue(property: string, value: string): string {
  if (
    !value ||
    value.length > 2048 ||
    /url\s*\(|expression|[\\"']/.test(value) ||
    property === 'content' ||
    property === 'src' ||
    property.startsWith('animation')
  )
    return '';
  for (const match of value.matchAll(/var\(\s*(--[\w-]+)/g))
    if (!/^--(?:p|bds|bn)-/.test(match[1])) return '';
  if (property.startsWith('--')) {
    if (
      !/^--(?:p|bds|bn)-/.test(property) ||
      /account|balance|routing|ssn|address|phone/i.test(property)
    )
      return '';
    const words =
      value
        .replace(/var\([^)]*\)/g, '')
        .replace(/#[a-f\d]{3,8}/gi, '')
        .replace(/[+-]?[\d.]+(?:rem|em|px|%|ms|s|vh|vw|dvh|dvw)?/gi, '')
        .match(/[a-z][a-z-]*/gi) ?? [];
    if (
      words.some(
        (word) =>
          ![
            'calc',
            'min',
            'max',
            'clamp',
            'rgb',
            'rgba',
            'hsl',
            'hsla',
            'oklch',
            'color-mix',
            'in',
            'srgb',
            'transparent',
            'currentcolor',
            'none',
            'auto',
            'normal',
            'solid',
            'dashed',
            'dotted',
            'inherit',
            'initial',
            'unset',
            'inset',
            'ease',
            'ease-in-out',
            'linear',
            'row',
            'column',
            'block',
            'inline',
            'flex',
            'grid',
            'bold',
            'left',
            'right',
            'center',
            'top',
            'bottom',
            'start',
            'end',
            'repeat',
            'nowrap',
          ].includes(word.toLowerCase()),
      )
    )
      return '';
  }
  if (property === 'font-family')
    return /^(?:Arial|Helvetica|Roboto|Inter|sans-serif|serif|monospace)(?:,\s*(?:Arial|Helvetica|Roboto|Inter|sans-serif|serif|monospace))*$/i.test(
      value,
    )
      ? value
      : 'sans-serif';
  return value;
}

const styleProperties = new Set([
  'display',
  'position',
  'top',
  'left',
  'right',
  'bottom',
  'width',
  'height',
  'min-width',
  'max-width',
  'min-height',
  'max-height',
  'margin',
  'padding',
  'overflow',
  'overflow-x',
  'overflow-y',
  'color',
  'background-color',
  'opacity',
  'font-size',
  'font-weight',
  'line-height',
  'text-align',
  'border',
  'border-radius',
  'flex',
  'flex-direction',
  'align-items',
  'justify-content',
  'gap',
  'z-index',
]);
function inlineStyle(value: unknown): string {
  if (typeof value !== 'string' || typeof document === 'undefined') return '';
  const style = document.createElement('span').style;
  style.cssText = value;
  return [...styleProperties]
    .flatMap((property) => {
      const content = safeCssValue(property, style.getPropertyValue(property));
      return content ? [`${property}:${content}`] : [];
    })
    .join(';');
}

/** rrweb uses property diffs for mutations; an empty full-style string clears unrelated styles. */
function inlineStyleMutation(value: unknown): string | RecordData {
  if (typeof value === 'string') return inlineStyle(value);
  const output: RecordData = {};
  if (typeof document === 'undefined') return output;
  const style = document.createElement('span').style;
  for (const [property, raw] of Object.entries(object(value))) {
    if (!styleProperties.has(property)) continue;
    if (raw === false) {
      output[property] = false;
      continue;
    }
    const tuple = Array.isArray(raw);
    const content = tuple ? raw[0] : raw;
    const priority = tuple ? raw[1] : '';
    if (
      typeof content !== 'string' ||
      (tuple && raw.length !== 2) ||
      (priority !== '' && priority !== 'important')
    )
      continue;
    if (content === '') {
      output[property] = false;
      continue;
    }
    const safe = safeCssValue(property, content);
    if (!safe) continue;
    style.cssText = '';
    style.setProperty(property, safe, priority);
    const parsed = style.getPropertyValue(property);
    if (parsed) output[property] = tuple ? [parsed, priority] : parsed;
  }
  return output;
}

function attributes(value: unknown): RecordData {
  const input = object(value),
    output: RecordData = {};
  for (const [key, raw] of Object.entries(input)) {
    if (raw === null) {
      if (
        [
          'class',
          'style',
          'href',
          'src',
          'value',
          'title',
          'placeholder',
          'aria-label',
          'alt',
        ].includes(key)
      )
        output[key] = null;
      continue;
    }
    if (key === 'class' && typeof raw === 'string')
      output[key] = raw
        .split(/\s+/)
        .filter((token) => REPLAY_STATIC_CLASSES.has(token))
        .join(' ');
    else if (/^_ng(?:content|host)-[\w-]+$/.test(key)) output[key] = '';
    else if (key === 'style') output[key] = inlineStyleMutation(raw);
    else if (['href', 'src', 'action', 'formaction'].includes(key)) {
      output[key] = sentryRoute(raw);
      for (const [name, id] of Object.entries(telemetryIdsFromUrl(raw)))
        output[`data-${name.replace(/_/g, '-')}`] = id;
    } else if (key === '_cssText') output[key] = stylesheet(raw);
    else if (
      key === 'value' ||
      ['title', 'placeholder', 'aria-label', 'alt'].includes(key)
    )
      output[key] = mask(raw);
    else if (key.startsWith('data-')) {
      const name = key.slice(5).replace(/-/g, '_');
      const id = telemetryInternalIds({ [name]: raw })[name];
      if (id) output[key] = id;
    } else if (
      [
        'width',
        'height',
        'colspan',
        'rowspan',
        'rr_width',
        'rr_height',
      ].includes(key) &&
      typeof raw === 'string' &&
      /^\d{1,6}(?:\.\d+)?(?:px|%)?$/.test(raw)
    )
      output[key] = raw;
    else if (
      /^(?:aria-(?:expanded|selected|disabled|checked|hidden)|disabled|checked|multiple|selected|readonly|required)$/.test(
        key,
      ) &&
      ['', 'true', 'false', 'mixed', true, false].includes(
        raw as string | boolean,
      )
    )
      output[key] = raw;
    else if (
      key === 'type' &&
      typeof raw === 'string' &&
      [
        'text',
        'password',
        'email',
        'number',
        'checkbox',
        'radio',
        'button',
        'submit',
        'search',
        'date',
        'hidden',
      ].includes(raw)
    )
      output[key] = raw;
    else if (
      key === 'role' &&
      typeof raw === 'string' &&
      [
        'button',
        'dialog',
        'tab',
        'tabpanel',
        'table',
        'row',
        'cell',
        'list',
        'listitem',
        'status',
        'alert',
        'presentation',
        'combobox',
        'option',
        'textbox',
        'grid',
        'tree',
        'treeitem',
      ].includes(raw)
    )
      output[key] = raw;
  }
  return output;
}

function ruleIndex(value: unknown): number | number[] | undefined {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0)
    return value;
  if (
    Array.isArray(value) &&
    value.every(
      (item) => typeof item === 'number' && Number.isInteger(item) && item >= 0,
    )
  )
    return value;
  return undefined;
}

function node(
  value: unknown,
  state: ReplayPolicyState,
  parentTag = '',
  depth = 0,
): RecordData {
  if (++state.nodes > 100000 || depth > 256)
    throw new Error('Replay node limit exceeded');
  const input = object(value);
  const output = technical(input, ['type', 'id', 'rootId']);
  const tag =
    typeof input['tagName'] === 'string' &&
    /^[a-z][a-z\d-]{0,80}$/i.test(input['tagName'])
      ? input['tagName'].toLowerCase()
      : '';
  if (tag) output['tagName'] = tag;
  if (input['type'] === 1)
    Object.assign(output, { name: 'html', publicId: '', systemId: '' });
  if (input['textContent'] !== undefined) {
    if (parentTag === 'style') {
      if (typeof input['id'] === 'number') state.styleTextIds.add(input['id']);
      output['textContent'] = stylesheet(input['textContent']);
    } else output['textContent'] = mask(input['textContent']);
  }
  if (input['attributes'])
    output['attributes'] = attributes(input['attributes']);
  for (const flag of ['isSVG', 'isShadow', 'isShadowHost', 'isShadowRoot'])
    if (typeof input[flag] === 'boolean') output[flag] = input[flag];
  if (input['childNodes'])
    output['childNodes'] = list(input['childNodes']).map((child) =>
      node(child, state, tag, depth + 1),
    );
  return output;
}

function custom(event: ReplayRecordingEvent): ReplayRecordingEvent | null {
  const data = object(event.data),
    payload = object(data['payload']);
  if (data['tag'] === 'breadcrumb') {
    const breadcrumb = filterSentryBreadcrumb(payload as Breadcrumb);
    if (breadcrumb) {
      breadcrumb.timestamp = numeric(breadcrumb.timestamp);
      if (
        !['debug', 'info', 'warning', 'error', 'fatal', 'log'].includes(
          breadcrumb.level ?? '',
        )
      )
        delete breadcrumb.level;
    }
    return breadcrumb
      ? {
          type: 5,
          timestamp: event.timestamp,
          data: { tag: 'breadcrumb', payload: breadcrumb },
        }
      : null;
  }
  if (data['tag'] === 'performanceSpan') {
    const op = payload['op'];
    const span = filterSentrySpan({
      trace_id: '0'.repeat(32),
      span_id: '0'.repeat(16),
      start_timestamp: 0,
      op:
        typeof op === 'string' && op.startsWith('navigation.')
          ? 'navigation'
          : typeof op === 'string'
            ? op
            : undefined,
      description:
        typeof payload['description'] === 'string'
          ? payload['description']
          : undefined,
      data: object(payload['data']) as Parameters<
        typeof filterSentrySpan
      >[0]['data'],
    });
    return {
      type: 5,
      timestamp: event.timestamp,
      data: {
        tag: 'performanceSpan',
        payload: {
          op: span.op,
          description: span.description,
          data: span.data,
          ...technical(payload, ['startTimestamp', 'endTimestamp']),
        },
      },
    };
  }
  if (data['tag'] === 'options') {
    const safe: RecordData = {};
    for (const [key, value] of Object.entries(payload)) {
      if (
        [
          'shouldRecordCanvas',
          'useCompressionOption',
          'blockAllMedia',
          'maskAllText',
          'maskAllInputs',
          'useCompression',
          'networkDetailHasUrls',
          'networkCaptureBodies',
          'networkRequestHasHeaders',
          'networkResponseHasHeaders',
        ].includes(key) &&
        typeof value === 'boolean'
      )
        safe[key] = value;
      if (
        ['sessionSampleRate', 'errorSampleRate'].includes(key) &&
        typeof value === 'number' &&
        value >= 0 &&
        value <= 1
      )
        safe[key] = value;
    }
    return {
      type: 5,
      timestamp: event.timestamp,
      data: { tag: 'options', payload: safe },
    };
  }
  return null;
}

/** Supported hook for custom events. DOM events are handled again at transport. */
export function filterReplayCustomEvent<T extends ReplayRecordingEvent>(
  event: T,
): T | null {
  return custom(event) as T | null;
}

export function filterReplayRecordingEvent(
  event: ReplayRecordingEvent,
  state: ReplayPolicyState,
): ReplayRecordingEvent | null {
  if (!event || numeric(event.timestamp) === undefined) return null;
  const data = object(event.data),
    base = { type: event.type, timestamp: event.timestamp };
  switch (event.type) {
    case 0:
    case 1:
      return base;
    case 2:
      state.nodes = 0;
      state.styleTextIds.clear();
      return {
        ...base,
        data: {
          node: node(data['node'], state),
          initialOffset: technical(object(data['initialOffset']), [
            'top',
            'left',
          ]),
        },
      };
    case 4:
      return {
        ...base,
        data: {
          href: sentryRoute(data['href']),
          ...technical(data, ['width', 'height']),
        },
      };
    case 5:
      return custom(event);
    case 6:
      return null; // Console/plugins can carry arbitrary arguments.
    case 3: {
      const source = data['source'];
      const output: RecordData = {
        source,
        ...technical(data, [
          'id',
          'x',
          'y',
          'width',
          'height',
          'type',
          'styleId',
        ]),
      };
      if (source === 0) {
        output['adds'] = list(data['adds']).map((item) => {
          const add = object(item);
          return {
            ...technical(add, ['parentId', 'nextId']),
            ...(add['nextId'] === null ? { nextId: null } : {}),
            node: node(add['node'], state),
          };
        });
        output['removes'] = list(data['removes']).map((item) =>
          technical(object(item), ['parentId', 'id']),
        );
        output['texts'] = list(data['texts']).map((item) => {
          const text = object(item);
          return {
            ...technical(text, ['id']),
            value: state.styleTextIds.has(text['id'] as number)
              ? stylesheet(text['value'])
              : mask(text['value']),
          };
        });
        output['attributes'] = list(data['attributes']).map((item) => {
          const attr = object(item);
          return {
            ...technical(attr, ['id']),
            attributes: attributes(attr['attributes']),
          };
        });
      } else if (source === 1 || source === 6 || source === 12)
        output['positions'] = list(data['positions']).map((item) =>
          technical(object(item), ['id', 'x', 'y', 'timeOffset']),
        );
      else if (source === 5) {
        output['text'] = mask(data['text']);
        if (typeof data['isChecked'] === 'boolean')
          output['isChecked'] = data['isChecked'];
      } else if (source === 8) {
        output['adds'] = list(data['adds']).map((item) => {
          const rule = object(item);
          return {
            rule: stylesheet(rule['rule']),
            index: ruleIndex(rule['index']),
          };
        });
        output['removes'] = list(data['removes']).map((item) => ({
          index: ruleIndex(object(item)['index']),
        }));
      } else if (source === 13) {
        const set = object(data['set']);
        const property = set['property'];
        if (
          typeof property === 'string' &&
          styleProperties.has(property) &&
          typeof set['value'] === 'string'
        )
          output['set'] = {
            property,
            value: inlineStyle(`${property}:${set['value']}`)
              .split(':')
              .slice(1)
              .join(':'),
          };
        const remove = object(data['remove']);
        if (
          typeof remove['property'] === 'string' &&
          styleProperties.has(remove['property'])
        )
          output['remove'] = { property: remove['property'] };
      } else if (source === 14) {
        output['styleIds'] = list(data['styleIds']).filter(
          (id) => typeof id === 'number' && Number.isInteger(id) && id >= 0,
        );
        output['styles'] = list(data['styles']).map((item) => {
          const style = object(item);
          return {
            ...technical(style, ['styleId']),
            rules: list(style['rules']).map((item) => {
              const rule = object(item);
              return {
                rule: stylesheet(rule['rule']),
                index: ruleIndex(rule['index']),
              };
            }),
          };
        });
      } else if (![2, 3, 4].includes(source as number)) return null;
      return { ...base, data: output };
    }
    default:
      return null;
  }
}

export function filterReplayMetadata(input: RecordData): RecordData {
  const shared = filterSentryError({ ...input, type: undefined } as ErrorEvent);
  const ids = (value: unknown) =>
    list(value).filter(
      (id) => typeof id === 'string' && /^[\da-f]{32}$/i.test(id),
    );
  return {
    type: 'replay_event',
    event_id: input['replay_id'],
    platform: 'javascript',
    replay_id: input['replay_id'],
    ...technical(input, ['segment_id', 'timestamp', 'replay_start_timestamp']),
    replay_type: input['replay_type'] === 'session' ? 'session' : 'buffer',
    urls: list(input['urls']).map(sentryRoute),
    segment_names: list(input['segment_names']).map(sentryRoute),
    error_ids: ids(input['error_ids']),
    trace_ids: ids(input['trace_ids']),
    release: shared.release,
    environment: shared.environment,
    dist: shared.dist,
    sdk: shared.sdk,
    user: shared.user,
    tags: shared.tags,
    extra: shared.extra,
    contexts: shared.contexts,
  };
}
